import { Injectable } from "@angular/core";

@Injectable({
  providedIn: 'root'
})
export class SecureIdService {
  private static topOrgRootId: string | null = null;
  private static username: string | null = null;
  private static permissions: string | null = null;
  private static treeRoots: string | null = null;

  /**
   * Stores the top organization root ID securely in memory
   */
  static storeTopOrgRootId(rootId: string): void {
    this.topOrgRootId = rootId ? rootId.trim() : '';
  }

  /**
   * Retrieves the stored top organization root ID
   */
  static getTopOrgRootId(): string | null {
    return this.topOrgRootId;
  }

  /**
   * Stores username securely in memory
   */
  static storeUsername(username: string): void {
    this.username = this.sanitizeInput(username);
  }

  /**
   * Retrieves the stored username
   */
  static getUsername(): string | null {
    return this.username;
  }

  /**
   * Stores user permissions securely in memory
   */
  static storePermissions(permissions: string): void {
    this.permissions = this.sanitizeInput(permissions);
  }

  /**
   * Retrieves the stored permissions
   */
  static getPermissions(): string | null {
    return this.permissions;
  }

  /**
   * Stores tree roots data securely in memory
   */
  static storeTreeRoots(treeRoots: string): void {
    this.treeRoots = this.sanitizeInput(treeRoots);
  }

  /**
   * Retrieves the stored tree roots
   */
  static getTreeRoots(): string | null {
    return this.treeRoots;
  }

  /**
   * Checks if organization root ID is valid (not null or empty)
   */
  static hasValidRootId(): boolean {
    return this.topOrgRootId !== null && this.topOrgRootId.trim() !== '';
  }

  /**
   * Checks if user data is available
   */
  static hasUserData(): boolean {
    return this.username !== null && this.username.trim() !== '';
  }

  /**
   * Clears all stored organization and user data
   */
  static clearAll(): void {
    this.topOrgRootId = null;
    this.username = null;
    this.permissions = null;
    this.treeRoots = null;
  }

  /**
   * Sanitizes general string input
   */
  private static sanitizeInput(input: string): string {
    if (!input) return '';
    
    // Basic sanitization for general text input
    return input.trim().replace(/[<>]/g, '');
  }

  /**
   * Gets the navigation route based on root ID
   */
  static getNavigationRoute(): string[] {
    const rootId = this.getTopOrgRootId();
    
    if (!rootId || rootId === '~') {
      return ['main/mmodal'];
    } else {
      return ['main/company/' + rootId];
    }
  }

  /**
   * Migrates data from sessionStorage if needed (for backward compatibility)
   */
  static migrateFromSessionStorage(): void {
    try {
      // Migrate existing sessionStorage data
      const sessionRootId = sessionStorage.getItem('rootId');
      const sessionUsername = sessionStorage.getItem('username');
      const sessionPermissions = sessionStorage.getItem('permissions');
      const sessionTreeRoots = sessionStorage.getItem('treeRoots');

      if (sessionRootId && !this.topOrgRootId) {
        this.storeTopOrgRootId(sessionRootId);
      }
      
      if (sessionUsername && !this.username) {
        this.storeUsername(sessionUsername);
      }
      
      if (sessionPermissions && !this.permissions) {
        this.storePermissions(sessionPermissions);
      }
      
      if (sessionTreeRoots && !this.treeRoots) {
        this.storeTreeRoots(sessionTreeRoots);
      }

      // Clear from sessionStorage after migration
      sessionStorage.removeItem('rootId');
      sessionStorage.removeItem('username');
      sessionStorage.removeItem('permissions');
      sessionStorage.removeItem('treeRoots');
      
    } catch (error) {
      console.warn('Error migrating from sessionStorage:', error);
    }
  }
}